import { PageHeader } from "@shared/components/PageHeader";
import { Stepper } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { ArrowLeft } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SystemFormData } from "../../types";
import { BasicsLocationStep } from "./basicLocationForm";
import { DocsContactsStep } from "./docsContactsForm";
import { OperationsStep } from "./operationsForm";

interface AddSystemLandingProps {
  editMode?: boolean;
  initialData?: Partial<SystemFormData>;
  systemId?: string;
}

const AddSystemLanding: React.FC<AddSystemLandingProps> = ({
  editMode = false,
  initialData = {},
  systemId,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [globalFormData, setGlobalFormData] = useState<any>({
    systemId: "",
    systemName: "",
    facilityId: "",
    systemType: "",
    status: "Operational",
    commissionedDate: "",
    backupStatus: "none",
    operationalHours: "",
    contactPerson: "",
    remoteContactUserId: "",
    contactEmail: "",
    contactPhone: "",
    facility: "",
    address: "",
    latitude: "",
    longitude: "",
    location: "",
    description: "",
    designCapacity: 0,
    capacityUnit: "MGD",
    currentThroughput: 0,
    monitoredParameters: [],
    criticalityScores: {
      impactOnSupply: 3,
      healthRisk: 3,
      redundancy: 3,
      dependency: 3,
      customerImpact: 3,
    },
    documents: [],
    vendorContacts: "",
    permitId: "",
    ...initialData,
  });

  const updateGlobalFormData = (data: Partial<SystemFormData>) => {
    setGlobalFormData((prev) => ({ ...prev, ...data }));
  };

  const handleComplete = async (allStepData: any) => {
    try {
      const finalData = {
        ...globalFormData,
        // Merge any additional step data if needed
      };

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast({
        title: editMode ? "System Updated" : "System Created",
        description: editMode
          ? `System "${finalData.systemName}" updated successfully`
          : `System "${finalData.systemName}" created successfully (ID: ${finalData.systemId})`,
      });

      // Redirect after success
      setTimeout(() => {
        window.location.href = "/ax/asset-registry/systems";
      }, 1500);
    } catch (error) {
      toast({
        title: "Error",
        description: editMode
          ? "Failed to update system"
          : "Failed to create system",
        variant: "destructive",
      });
    }
  };

  const stepperSteps = [
    {
      id: "basics-location",
      title: "Basics,Contacts & Location",
      component: (
        <BasicsLocationStep
          globalFormData={globalFormData}
          updateGlobalFormData={updateGlobalFormData}
          editMode={editMode}
        />
      ),
      optional: false,
      slug: "basics-location",
    },
    {
      id: "operations",
      title: "Operations & Criticality",
      component: (
        <OperationsStep
          globalFormData={globalFormData}
          updateGlobalFormData={updateGlobalFormData}
          editMode={editMode}
        />
      ),
      optional: false,
      slug: "operations",
    },
    {
      id: "docs",
      title: "Documents",
      component: (
        <DocsContactsStep
          globalFormData={globalFormData}
          updateGlobalFormData={updateGlobalFormData}
          editMode={editMode}
        />
      ),
      optional: false,
      slug: "docs",
    },
  ];

  return (
    <div className="p-2">
      <div className="space-y-6">
        <div className="mb-4 flex items-center gap-4">
          {/* Back Arrow */}
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/ax/asset-registry/systems")}
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title={editMode ? "Edit System" : "Add New System"}
              subtitle={
                editMode
                  ? `Update system record: ${
                      systemId || globalFormData.systemId
                    }`
                  : "Create a new system record in your infrastructure management system"
              }
            />
          </div>
        </div>

        <Stepper
          steps={stepperSteps}
          onComplete={handleComplete}
          autoSave={true}
          persistProgress={true}
          enableApiSave={false}
          saveOnStepChange={false}
          trackDirtyState={false}
          storageKey={
            editMode ? `edit-system-${systemId}` : "add-system-progress"
          }
          allowSkip={false}
          enableRouting={true}
          className="w-full"
          allowStepClick={false}
          onStepChange={undefined}
          onValidation={undefined}
          onStepSave={undefined}
        />
      </div>
    </div>
  );
};

export default AddSystemLanding;
